import type { TFunction } from 'i18next';

import type { CodeStepStatus } from '../../../core-components/theme/components/CodeStep';
import { formatMmSs } from '../../../utilities/countdown';
import type { CodeProblem, CodeStatus } from '../domain/codeStatus';
import type { TimedRefusalCode } from '../domain/refusal';

import { codeDetailText, minutesOf, refusalText } from './refusalText';

export const noConnectionText = (t: TFunction): string => t('passcodeAuth.error.noConnection', 'No connection. Check your network and try again.');

export const holdText = (t: TFunction, reason: TimedRefusalCode, seconds: number): string => {
  const count = minutesOf(seconds);
  switch (reason) {
    case 'LOCKED':
      return count === 1
        ? t('passcodeAuth.hold.lockedOne', 'Too many wrong codes. Try again in 1 minute.')
        : t('passcodeAuth.hold.locked', 'Too many wrong codes. Try again in {{count}} minutes.', { count });
    case 'TOO_MANY_SENDS':
      return count === 1
        ? t('passcodeAuth.hold.tooManyOne', 'Too many codes were asked for on this number or network. Try again in 1 minute.')
        : t('passcodeAuth.hold.tooMany', 'Too many codes were asked for on this number or network. Try again in {{count}} minutes.', { count });
    case 'RESEND_TOO_SOON':
      return t('passcodeAuth.refusal.resendWait', 'Wait {{time}} before asking for another code.', { time: formatMmSs(seconds) });
  }
};

export const holdLapsedText = (t: TFunction): string => t('passcodeAuth.hold.lapsed', 'You can ask for a code again now.');

export const codeProblemText = (t: TFunction, problem: CodeProblem): string => {
  switch (problem.kind) {
    case 'transport':
      return noConnectionText(t);
    case 'refused':
      return refusalText(t, problem.refusal);
    case 'switchNotSaved':
      return t('passcodeAuth.switch.notSaved', 'The code was accepted, but alerts still arrive where they did. Try again.');
    case 'switchUnconfirmed':
      return t('passcodeAuth.switch.unconfirmed', 'The code was accepted, but we couldn’t check the switch. Reload to see where alerts arrive.');
  }
};

const detailOf = (t: TFunction, problem: CodeProblem): string | null => (problem.kind === 'refused' ? codeDetailText(t, problem.refusal) : null);

export const codeStepStatusOf = (t: TFunction, status: CodeStatus): CodeStepStatus => {
  switch (status.kind) {
    case 'entering':
      return { kind: 'entering' };
    case 'verifying':
      return { kind: 'verifying' };
    case 'expired':
      return { kind: 'expired' };
    case 'blocked':
      return { kind: 'blocked', message: holdText(t, status.reason, status.seconds) };
    case 'refused':
      return { kind: 'refused', message: codeProblemText(t, status.problem), detail: detailOf(t, status.problem) };
  }
};
